import { sha256 } from '@uhg-uhcpp-sde-core/monstera-utility';

const pageNamePrefix = 'digitalsecurity';

const safeJoin = (value) => {
  return Array.isArray(value) ? value.join(',') : '';
};

// Simple mobile detection that works across all browsers
const isMobileDevice = () => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

// Check if current page is the 404 error page
const is404Page = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  // This works for any URL that renders the Custom404Page component
  if (window.__IS_404_PAGE__ === true) {
    return true;
  }
  return false;
};

// Get the browser language
const getBrowserLanguage = () => {
  if (typeof navigator === 'undefined') {
    return 'english';
  }
  // Get the language from browser API (e.g., 'en-US', 'es-ES', 'fr-FR')
  const lang = navigator.language || navigator.userLanguage || 'en';
  // Extract the primary language code (e.g., 'en' from 'en-US')
  const primaryLang = lang.split('-')[0].toLowerCase();

  // Map common language codes to full names
  const languageMap = {
    en: 'english',
    es: 'spanish',
    fr: 'french',
    de: 'german',
    it: 'italian',
    pt: 'portuguese',
    zh: 'chinese',
    ja: 'japanese',
    ko: 'korean',
    ar: 'arabic',
    ru: 'russian',
  };
  return languageMap[primaryLang] || 'english';
};

const getUserObject = (data = {}) => {
  return {
    loginStatus: 'logged in',
    userType: 'provider',
    groupId: '',
    carrierId: '',
    hsid: '',
    enterpriseId: '',
    payerGroup: '',
    accountId: data.accountNumber || '',
    customAttributes: {
      corpOwnerName: data.corporateProviderName
        ? sha256(data.corporateProviderName)
        : '',
      corpOwnerMpin: data.corporateProviderMpin
        ? sha256(data.corporateProviderMpin)
        : '',
      providerMpin: data.careProviderMpin ? sha256(data.careProviderMpin) : '',
      providerTaxId: data.taxIdNumber ? sha256(data.taxIdNumber) : '',
      payerNameId:
        data.payerName && data.payerId
          ? `${data.payerName} - ${data.payerId}`
          : '',
      providerOhid: data.uuid || '',
      adminProviderOhid: data?.adminProviderUuid ? data.adminProviderUuid : '',
      providerNpi: '',
      providerCaqhId: '',
      networkStatus: '',
      medicalSpecialty: '',
      providerType: '',
      corpOwnerthirdPartyFlag: data.isThirdParty === true ? 'yes' : 'no',
    },
  };
};

const getMemberObject = (data = {}) => {
  return {
    memberId: '',
    memberPlanType: '',
    memberPatientType: '',
    memberPolicyType: '',
    memberType: '',
    memberGroupNumber: '',
    patientAccountNo: '',
    memberCareOppurtunitiesFlag: '',
    memberPriorAuthorizationRequiredFlag: '',
    memberReferralRequiredFlag: '',
    reconStatus: '',
    claimNum: '',
    claimProcessingSource: '',
    claimStatus: '',
    claimType: '',
    remarkCode: '',
    referenceNumber: '',
    diagnosisCode: '',
    procedureCode: '',
    claimProcessingSource: '',
  };
};

const getPageData = (data = {}) => {
  return {
    page: {
      experienceType: isMobileDevice() ? 'mobile' : 'desktop',
      is404: is404Page(),
      lang: getBrowserLanguage(),
      name: '',
      siteSectionL1: 'secure',
      siteSectionL2: 'digital security',
      siteSectionL3: '',
      siteSectionL4: '',
      test: '',
      group: '',
      referringSite:
        typeof document !== 'undefined' ? document.referrer || '' : '',
      userSelections: '',
      customAttributes: {
        dataPlatform: '',
        emulationFlag: data.isTechnicalUser ? 'Emulated' : 'Not Emulated',
        enabledClaimActions: '',
        availableClaimActions: '',
        disabledClaimActions: '',
      },
    },
    user: getUserObject(data),
    member: getMemberObject(data),
  };
};

const getPageLoadData = (data = {}, pageOverrides = {}) => {
  const baseData = getPageData(data);

  return {
    ...baseData,
    page: {
      ...baseData.page,
      name: `${pageNamePrefix}:${data?.pageName || ''}`,
      ...pageOverrides,
    },
  };
};

export const greenfieldAdobeTags = {
  ON_PAGE_LOAD: (pageData) => {
    return {
      event: 'page track',
      data: getPageLoadData(pageData, {
        siteSectionL3: pageData?.siteSectionL3 || '',
        siteSectionL4: pageData?.siteSectionL4 || '',
      }),
    };
  },
  ON_SUB_PAGE_LOAD: () => {
    return {
      event: 'subpage track',
      data: {
        subpage: {
          contentSection:
            'your security key is on the way. please allow for up to 10 business days',
          customAttributes: {},
        },
      },
    };
  },
  FORM_VALIDATION_ERROR: (data) => {
    return {
      event: 'site error',
      data: {
        error: {
          message: 'Missing or invalid field(s)',
          source: data?.source ?? '',
          code: '',
          subtype: 'ui errors',
          customAttributes: {
            errorApiCode: '',
            productName: 'digital security',
            errorFields: safeJoin(data?.fields),
          },
        },
        component: {
          name: '',
          customAttributes: {},
        },
      },
    };
  },
  API_TRANSACTION_ERROR: (values) => {
    return {
      event: 'site error',
      data: {
        error: {
          message: values?.errorMessage || '',
          source: values?.source ?? '',
          code: values?.errorHttpsCode || '',
          subtype: 'site',
          customAttributes: {
            errorApiCode: values?.errorApiCode || '',
            productName: 'digital security',
            errorFields: safeJoin(values?.fields),
          },
        },
        component: {
          name: '',
          customAttributes: {},
        },
      },
    };
  },
  NEW_TIN_ERROR: (values) => {
    return {
      event: 'site error',
      data: {
        error: {
          message: values?.errorMessage || '',
          source: values?.source ?? '',
          code: values?.errorHttpsCode || '',
          subtype: 'site',
          customAttributes: {
            errorApiCode: values?.errorApiCode || '',
            productName: 'digital security',
          },
        },
        component: {
          name: '',
          customAttributes: {},
        },
      },
    };
  },
  EXIT_LINK_CLICK: (values) => {
    return {
      event: 'link track',
      data: {
        link: {
          name: values?.exitLink || '',
          location: values?.location || '',
          type: 'download',
          userSelections: '',
          destinationUrl: values?.exitUrl || '',
          customAttributes: {
            interactionContext: 'download clicks',
          },
        },
        component: {
          name: '',
          customAttributes: {},
        },
      },
    };
  },
  TIN_REGISTRATION_START: (values) => {
    return {
      event: 'form started',
      data: {
        form: {
          name: values?.jobName || '',
          customAttributes: {},
        },
        component: {
          name: '',
          customAttributes: {},
        },
        user: {
          customAttributes: {
            providerType: values?.orgType || '',
          },
        },
      },
    };
  },
  TIN_REGISTRATION_COMPLETION: (values) => {
    const jobFunctionString = values?.jobFunctions
      ? `jobFunction: ${values?.jobFunctions}`
      : '';
    const toolAccessString = values?.portalTaskAccess
      ? `toolAccess: ${values?.portalTaskAccess} | `
      : '';
    return {
      event: 'form submit',
      data: {
        form: {
          name: values?.jobName || '',
          step: `step 1: ${values?.jobName || ''}`,
          userSelections: `${toolAccessString}${jobFunctionString}`,
          customAttributes: {
            statusFlag: values?.rcmOrBillingFlag
              ? `rcm or billing flag: ${values?.rcmOrBillingFlag}`
              : '',
          },
        },
        component: {
          name: '',
          customAttributes: {},
        },
        user: {
          customAttributes: {
            providerType: values?.orgType || '',
            providerOhid: values?.providerUuid || '',
            providerNpi: values?.npi || '',
            providerTaxId: sha256(values?.registrationTaxId),
            corpOwnerName: values?.companyName
              ? sha256(values?.companyName)
              : '',
          },
        },
      },
    };
  },
  TIN_REGISTRATION_FINAL_COMPLETE: (values) => {
    const jobFunctionString = values?.jobFunctions
      ? `jobFunction: ${values?.jobFunctions}`
      : '';
    const toolAccessString = values?.portalTaskAccess
      ? `toolAccess: ${values?.portalTaskAccess} | `
      : '';
    return {
      event: 'form complete',
      data: {
        form: {
          name: values?.jobName || '',
          userSelections: `${toolAccessString}${jobFunctionString}`,
          customAttributes: {
            statusFlag: values?.rcmOrBillingFlag
              ? `rcm or billing flag: ${values?.rcmOrBillingFlag}`
              : '',
          },
        },
        component: {
          name: '',
          customAttributes: {},
        },
        user: {
          customAttributes: {
            providerType: values?.orgType || '',
            providerOhid: values?.providerUuid || '',
            providerNpi: values?.npi || '',
            providerTaxId: sha256(values?.registrationTaxId),
            corpOwnerName: values?.companyName
              ? sha256(values?.companyName)
              : '',
          },
        },
      },
    };
  },
  PAA_REGISTRATION_STARTS: (values) => {
    return {
      event: 'form started',
      data: {
        form: {
          name: values?.jobName,
          customAttributes: {},
        },
        component: {
          name: '',
          customAttributes: {},
        },
        user: {
          customAttributes: {
            providerType: values?.orgType || '',
          },
        },
      },
    };
  },
  PAA_REGISTRATION_COMPLETION: (values) => {
    return {
      event: 'form submit',
      data: {
        form: {
          name: values?.jobName || '',
          step: 'step 1: paa registration - company verification',
          userSelections: '',
          customAttributes: {
            verificationMethod: values?.companyVerificationMethod || '',
          },
        },
        component: {
          name: '',
          customAttributes: {},
        },
        user: {
          customAttributes: {
            providerOhid: values?.providerUuid || '',
            providerTaxId: sha256(values?.registrationTaxId),
          },
        },
      },
    };
  },
  PAA_REGISTRATION_FINAL_COMPLETE: (values) => {
    return {
      event: 'form complete',
      data: {
        form: {
          name: 'paa registration',
          userSelections: '',
          customAttributes: {
            verificationMethod: values?.companyVerificationMethod || '',
          },
        },
        component: {
          name: '',
          customAttributes: {},
        },
        user: {
          customAttributes: {
            providerOhid: values?.providerUuid || '',
            providerTaxId: sha256(values?.registrationTaxId),
          },
        },
      },
    };
  },
  CTA_CLICKS: (values) => {
    // Supported values:
    // - location: 'job functions' with payload as single job object, checked-job map, or array of jobs.
    // - location: 'healthcare professional portal access' with payload.permissions[] or payload[] of permission objects.
    let selections = '';
    let itemStatus = '';
    const normalizedLocation = (values?.location || '').toLowerCase();
    const payload = values?.payload;

    if (normalizedLocation === 'job functions') {
      const selectedJobs =
        Array.isArray(payload) && payload.length > 0
          ? payload
          : payload && typeof payload === 'object'
          ? Object.values(payload).filter((job) => {
              if (!job || typeof job !== 'object') {
                return false;
              }
              if (Object.prototype.hasOwnProperty.call(job, 'checked')) {
                return job.checked === true;
              }
              return Boolean(job.name);
            })
          : [];

      const jobNames = selectedJobs
        .map((job) => job?.name)
        .filter(Boolean)
        .join(', ');

      if (!jobNames && payload?.name) {
        selections = `jobFunction: ${payload.name}`;
      } else {
        selections = jobNames ? `jobFunction: ${jobNames}` : '';
      }

      const firstRcmValue = selectedJobs.find((job) => {
        return Boolean(job?.RCMValue);
      })?.RCMValue;

      itemStatus = firstRcmValue
        ? `rcm or billing flag: ${firstRcmValue}`
        : payload?.RCMValue
        ? `rcm or billing flag: ${payload.RCMValue}`
        : '';
    } else if (normalizedLocation === 'healthcare professional portal access') {
      const permissionList = Array.isArray(payload?.permissions)
        ? payload.permissions
        : Array.isArray(payload)
        ? payload
        : [];

      const permissionNames = permissionList
        .map((p) => p?.permission || p?.name)
        .filter(Boolean)
        .join(', ');

      selections = permissionNames ? `portalAccess: ${permissionNames}` : '';
    }

    return {
      event: 'link track',
      data: {
        link: {
          name: values?.value || '',
          location: values?.location || '',
          type: 'internal',
          userSelections: selections,
          destinationUrl: '',
          customAttributes: {
            interactionContext: `tin registration-${values?.context}`,
            itemStatus: itemStatus || payload?.itemStatus || '',
          },
        },
        component: {
          name: '',
          customAttributes: {},
        },
        user: {
          providerNpi: values?.payload?.npiValue || '',
          corpOwnerName: sha256(values?.payload?.companyName || ''),
        },
      },
    };
  },
  VERIFY_CLAIM: (values) => {
    const { fDOSData, lDOSData } = values || {};
    const userSelectionsString =
      fDOSData || lDOSData
        ? `firstDateOfService: ${fDOSData || ''} | lastDateOfService: ${
            lDOSData || ''
          }`
        : '';
    return {
      event: 'link track',
      data: {
        link: {
          name: 'next',
          location: values?.verificationType || '',
          type: 'internal',
          userSelections: userSelectionsString,
          destinationUrl: '',
          customAttributes: {
            interactionContext:
              'tin registration-healthcare professional or facility',
          },
        },
        component: {
          name: '',
          customAttributes: {},
        },
      },
    };
  },
  DOWNLOAD_CLICKS: (values) => {
    return {
      event: 'link track',
      data: {
        link: {
          name: values?.downloadFileName || '',
          location: values?.location || '',
          type: 'download',
          userSelections: '',
          destinationUrl: values?.fileUrl || '',
          customAttributes: {
            interactionContext: values?.fileContext || '',
          },
        },
        component: {
          name: '',
          customAttributes: {},
        },
      },
    };
  },
  LINK_CLICK: (values) => {
    return {
      event: 'link track',
      data: {
        link: {
          name: values?.linkName || '',
          location: values?.location || '',
          type: 'internal',
          userSelections: values?.userSelections || '',
          destinationUrl: '',
          customAttributes: {
            interactionContext: values?.context,
          },
        },
        component: {
          name: '',
          customAttributes: {},
        },
      },
    };
  },
  MODAL_OPEN: (values) => {
    return {
      event: 'modal track',
      data: {
        modal: {
          name: values?.modalName || '',
          customAttributes: {},
        },
        component: {
          name: '',
          customAttributes: {},
        },
      },
    };
  },
  COMPANY_VENDOR_MODAL_VIEW: (values) => {
    return {
      event: 'modal track',
      data: {
        modal: {
          name: values?.modalName || '',
          customAttributes: {},
        },
        component: {
          name: '',
          customAttributes: {},
        },
      },
    };
  },
  COMPANY_VENDOR_MODAL_CLOSE: (values) => {
    return {
      event: 'link track',
      data: {
        link: {
          name: values?.linkName || 'close',
          location: values?.modalName || '',
          type: 'modal close',
          userSelections: '',
          destinationUrl: '',
          customAttributes: {
            interactionContext: 'modal cta clicks',
          },
        },
        component: {
          name: '',
          customAttributes: {},
        },
      },
    };
  },
  MODAL_CLOSE: (values) => {
    return {
      event: 'link track',
      data: {
        link: {
          name: values?.linkName || 'close',
          location: values?.modalName || '',
          type: 'modal close',
          userSelections: '',
          destinationUrl: '',
          customAttributes: {
            interactionContext: 'modal cta clicks',
          },
        },
        component: {
          name: '',
          customAttributes: {},
        },
      },
    };
  },
  MAIL_VALIDATION: (values) => {
    return {
      event: 'link track',
      data: {
        link: {
          name: 'missing an address',
          location: 'us mail validation:select address on file',
          type: 'internal',
          userSelections: '',
          destinationUrl: '',
          customAttributes: {
            interactionContext:
              'tin registration-healthcare professional or facility',
          },
        },
        component: {
          name: '',
          customAttributes: {},
        },
      },
    };
  },

  RCM_SELECTION: (values) => {
    return {
      event: 'link track',
      data: {
        link: {
          name: values?.interactionName || '',
          location: values?.interactionValue || '',
          type: 'internal',
          userSelections: '',
          destinationUrl: '',
          customAttributes: {
            interactionContext: values?.interactionContext || '',
          },
        },
        component: {
          name: '',
          customAttributes: {},
        },
      },
    };
  },
  EXIT_CLICK: (values) => {
    return {
      event: 'link track',
      data: {
        link: {
          name: values?.exitLink || '',
          location: values?.linkLocation || '',
          type: 'external',
          userSelections: '',
          destinationUrl: values?.linkURL || '',
          customAttributes: {
            interactionContext: 'exit clicks',
          },
        },
        component: {
          name: '',
          customAttributes: {},
        },
      },
    };
  },
  RCM_CTA_CLICK: (values) => {
    return {
      event: 'link track',
      data: {
        link: {
          name: values?.interactionValue || 'close',
          location: values?.interactionName || '',
          type: 'modal close',
          userSelections: '',
          destinationUrl: '',
          customAttributes: {
            interactionContext: 'modal cta clicks',
          },
        },
        component: {
          name: '',
          customAttributes: {},
        },
      },
    };
  },
};
